import { BaseError } from './BaseError';
import { ApplicationError, ErrorSeverity } from './ApplicationError';

/**
 * Backoff strategy for retries
 */
export enum BackoffStrategy {
  CONSTANT = 'constant',
  EXPONENTIAL = 'exponential',
  LINEAR = 'linear',
}

/**
 * Options for retry operations
 */
export interface RetryOptions {
  maxRetries?: number;
  delay?: number;
  backoffStrategy?: BackoffStrategy;
  maxDelay?: number;
  onRetry?: (attempt: number, error: unknown) => void;
  shouldRetry?: (error: unknown) => boolean;
}

/**
 * Default retry options
 */
const defaultRetryOptions = {
  maxRetries: 3,
  delay: 1000, // 1 second
  backoffStrategy: BackoffStrategy.EXPONENTIAL,
  maxDelay: 30000, // 30 seconds
  shouldRetry: () => true,
  onRetry: () => {},
};

/**
 * Utility for retrying operations that may fail due to transient errors
 */
export class RetryUtil {
  /**
   * Retry a function until it succeeds or max retries is reached
   */
  public static async retry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
    // Merge with default options
    const retryOptions = {
      ...defaultRetryOptions,
      ...options,
      // Ensure these functions are defined
      onRetry: options.onRetry || defaultRetryOptions.onRetry,
      shouldRetry: options.shouldRetry || defaultRetryOptions.shouldRetry,
    };

    let attempt = 0;
    let lastError: unknown;

    while (attempt <= retryOptions.maxRetries) {
      try {
        if (attempt > 0) {
          // Calculate delay based on backoff strategy
          const delay = RetryUtil.calculateDelay(
            attempt,
            retryOptions.delay,
            retryOptions.backoffStrategy,
            retryOptions.maxDelay
          );

          // Wait for the calculated delay
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        // Try the operation
        return await fn();
      } catch (error) {
        lastError = error;

        // Check if we should retry
        if (attempt >= retryOptions.maxRetries || !retryOptions.shouldRetry(error)) {
          break;
        }

        // Call the onRetry callback
        retryOptions.onRetry(attempt + 1, error);

        // Increment attempt count
        attempt++;
      }
    }

    // Process the error after all retries have failed

    // Create a more informative error
    const appError = new ApplicationError(
      `Operation failed after ${attempt} attempts`,
      ErrorSeverity.MEDIUM,
      {
        isOperational: true,
        cause: lastError instanceof Error ? lastError : undefined,
        context: {
          attempts: attempt,
          maxRetries: retryOptions.maxRetries,
          originalError: lastError instanceof Error ? undefined : String(lastError),
        },
      }
    );

    // Throw the application error
    throw appError;
  }

  /**
   * Calculate retry delay based on backoff strategy
   */
  private static calculateDelay(
    attempt: number,
    baseDelay: number,
    strategy: BackoffStrategy,
    maxDelay: number
  ): number {
    let delay: number;

    switch (strategy) {
      case BackoffStrategy.LINEAR:
        // Linear: baseDelay * attempt
        delay = baseDelay * attempt;
        break;
      case BackoffStrategy.EXPONENTIAL:
        // Exponential: baseDelay * 2^(attempt-1)
        delay = baseDelay * Math.pow(2, attempt - 1);
        break;
      case BackoffStrategy.CONSTANT:
      default:
        // Constant: always baseDelay
        delay = baseDelay;
        break;
    }

    // Add some jitter to prevent synchronized retries
    const jitter = Math.random() * 100;

    // Ensure the delay doesn't exceed maxDelay
    return Math.min(delay + jitter, maxDelay);
  }

  /**
   * Determine if an error should be retried based on common transient error patterns
   */
  public static isTransientError(error: unknown): boolean {
    // Network errors are often transient
    if (error instanceof TypeError && error.message.includes('network')) {
      return true;
    }

    // Timeout errors are often transient
    if (error instanceof Error && error.message.toLowerCase().includes('timeout')) {
      return true;
    }

    // API rate limiting errors (usually 429 Too Many Requests)
    if (
      error instanceof BaseError &&
      error.name === 'ApiError' &&
      error.context?.statusCode === 429
    ) {
      return true;
    }

    // Some server errors can be transient (500, 502, 503, 504)
    if (
      error instanceof BaseError &&
      error.name === 'ApiError' &&
      [500, 502, 503, 504].includes(error.context?.statusCode as number)
    ) {
      return true;
    }

    return false;
  }
}
